<script setup>
import { defaultEmployeeImage } from "@/views/production/mixin/common";

import moment from "moment";
import { computed, ref, onMounted } from "vue";
import OccupationProgress from './OccupationProgress';
import Presence from './Presence';
import Absence from './Absence';

const props = defineProps({
  info: {
    type: Object,
    required: true,
  },
})

const baseUri = process.env.VUE_APP_BASE_URI;
const holidaysFrom = ref('');
const holidaysTo = ref('');
const publicHoliday = ref({});

const isCollaborator = computed(() => {
  return props.info.employee && props.info.employee.employeeType && props.info.employee.employeeType === "external_collaborator"
})


const headerStyle = computed(() => {
  if (!isWorking()) {
    if (isResting() || isRestingPublicHoliday()) {
      return {
        'border-left': '#808080' + ' solid 4px',
        'background': '#f5f4f4',
      };
    } else if (isAbsence()) {
      return {
        'border-left': 'cyan' + ' solid 4px',
        'background': 'lightcyan',
      };
    } else {
      return {};
    }
  }
  return { 'border-top': getWorklog()?.color + ' solid 4px' };
})

onMounted(() => {
  if (props.info.holidays && props.info.holidays.fromDate && props.info.holidays.toDate) {
    holidaysFrom.value = props.info.holidays.fromDate;
    holidaysTo.value = props.info.holidays.toDate;
  }
  if (props.info.publicHoliday && props.info.publicHoliday.date) {
    publicHoliday.value = props.info.publicHoliday;
  }
})
// eslint-disable-next-line no-unused-vars
const project = computed(() => {
  return getWorklog().projectName;
})
// eslint-disable-next-line no-unused-vars
const client = computed(() => {
  return getWorklog().clientName;
})
// eslint-disable-next-line no-unused-vars
const timeentry = computed(() => {
  return getWorklog().description;
})
const photo = computed(() => {
  if (!props.info.employee.photo) {
    return null;
  }
  return baseUri.replace(/\/$/, "") + '/images/employees/' + props.info.employee.photo;
})
const userFullName = computed(() => {
  return `${props.info.employee?.name} ${props.info.employee.lastname}`;
})
const totalSignins = computed(() => {
  return toHours(props.info.presence?.totalWorked ?? 0);
})
const totalWorklog = computed(() => {
  return toHours(props.info.totalWorked);
})
const occupationPercent = computed(() => {
  // return parseFloat(((props.info.totalWorked * 100) / (props.info.presence?.totalWorked ?? 0)).toFixed(2));
  const val = parseFloat(((props.info.totalWorked * 100) / (props.info.presence?.totalWorked ?? 0)).toFixed(2));
  return isNaN(val) ? 0 : ((val > 100) ? 100 : val);
})

const isOnVacations = computed(() => {
  return isResting();
})
// eslint-disable-next-line no-unused-vars
const isOnPublicHoliday = computed(() => {
  return isRestingPublicHoliday();
})
// eslint-disable-next-line no-unused-vars
const absences = computed(() => {
  return isAbsence();
})

// eslint-disable-next-line no-unused-vars
const cellStyle = ({ row, column, rowIndex, columnIndex }) => {

  if (rowIndex === 0 && columnIndex === 0 && row.end === null) {
    return { 'border-left': row.color + ' solid 5px' }
  }

  return {}
}
const toHours = (totalSeconds) => {
  const sec_num = parseInt(totalSeconds, 10); // don't forget the second param
  let hours = Math.floor(sec_num / 3600);
  let minutes = Math.floor((sec_num - (hours * 3600)) / 60);
  // var seconds = sec_num - (hours * 3600) - (minutes * 60);

  if (hours < 10) {
    hours = "0" + hours;
  }
  if (minutes < 10) {
    minutes = "0" + minutes;
  }
  return hours + 'h ' + minutes + 'm';
}
const isWorking = () => {
  return getWorklog() && (getWorklog().start !== null) && (getWorklog().end === null)
}
/*const occupationText = (percent) => {
  return `Ocupación ${percent}%`;
}*/
const isResting = () => {
  return props.info?.holidays?.length !== 0;
}
const isRestingPublicHoliday = () => {
  return props.info?.publicHoliday?.length !== 0;
}
const isAbsence = () => {
  return props.info?.absence?.length !== 0;
}

const getWorklog = () => {
  if (props.info.employee.canCreateTimeEntry) {
    return props.info.worklogTrackerNet?.[0];
  } else {
    return props.info.worklog?.[0];
  }
}

// eslint-disable-next-line no-unused-vars
const getAllWorklog = () => {
  if (props.info.employee.canCreateTimeEntry) {
    return props.info.worklogTrackerNet;
  } else {
    return props.info.worklog;
  }
}

const formatDateAndMonth = (value) => {
  if (value) {
    return moment(value, "DD-MM-YYYY").format('DD-MM');
  } else {
    return '';
  }
}

</script>

<template>
  <div class="user-info-extended">
    <div class="extended-user-info-card">
      <div class="d-flex row w-100">
      <div class="col-md-1 el-col-xs-8">
        <el-avatar :src="photo" :size="75">
          <img :src="defaultEmployeeImage" alt="Default Image">
        </el-avatar>
      </div>
      <div class="col-md-5 el-col-xs-16 align-items-center d-flex">
        <h2 style="margin-left: 10px">
          {{ userFullName }}
        </h2>
      </div>
      <div class="col-md-6 el-col-xs-24 justify-content-end d-flex">
        <h3 style="margin-left: auto">
          Total trabajado <i class="el-icon-thumb" /> {{ !isCollaborator ? totalSignins + ' / ' : '' }}
          <i class="el-icon-alarm-clock" /> {{ totalWorklog }}
          <OccupationProgress :percentaje="occupationPercent" v-if="!isCollaborator" />
        </h3>
      </div>
      </div>
    </div>
    <div v-if="!isOnVacations && !isOnPublicHoliday">
      <el-divider>Registro de horas en {{ info.employee.canCreateTimeEntry ? 'Trackernet' : 'Clockify' }}</el-divider>

      <el-table :data="getAllWorklog()" border :cell-style="cellStyle">
        <el-table-column label="Descripción" prop="description" />
        <el-table-column width="150" label="Cliente" prop="clientName" />
        <el-table-column width="150" label="Proyecto" prop="projectName" />
        <el-table-column width="100" label="Tiempo">
          <template #default="scope">
            {{ toHours(scope.row.duration) }}
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div v-else-if="isOnVacations">
      <el-divider>Vacaciones</el-divider>
      <div style="text-align: left" :style="headerStyle">
        <i class="el-icon-cold-drink" /> Desde {{ formatDateAndMonth(holidaysFrom) }} hasta {{
          formatDateAndMonth(holidaysTo)

        }}<br>
      </div>
    </div>
    <div v-else-if="isOnPublicHoliday">
      <el-divider>Festivo</el-divider>
      <div style="text-align: left" :style="headerStyle">
        <span v-if="publicHoliday.type === 'nacional'">
          <i class="el-icon-cold-drink" /> {{ formatDateAndMonth(publicHoliday.date) }} {{
            publicHoliday.description
          }}, Fiesta Nacional<br>
        </span>
        <span v-else>
          <i class="el-icon-cold-drink" /> {{ formatDateAndMonth(publicHoliday.dat) }} {{
            publicHoliday.description
          }} en {{ publicHoliday.workPlaceName }}<br>
        </span>
      </div>
    </div>


    <div v-if="!isOnVacations && !isOnPublicHoliday && !isCollaborator">
      <el-divider>Presencia</el-divider>
      <Presence :presence="info.presence" show-details />
    </div>

    <div v-if="absences">
      <el-divider>Ausencia</el-divider>
      <Absence :absence="info.absence" :style="headerStyle" />
    </div>
  </div>
</template>

<style lang="scss">
.user-info-extended {
  display: flex;
  flex-direction: column;
  width: 100%;

  /* Asegurarse que no haya espacios extra al final */
  >div:last-child {
    margin-bottom: 0;
  }

  .el-divider {
    margin: 16px 0;
  }

  .el-table {
    margin-bottom: 0;
  }
}

.extended-user-info-card {
  display: flex;
  align-items: center;
}

@media (max-width: 767px){
  .user-info-extended{
    .extended-user-info-card{
      padding-bottom: 20px;
    }
  }
}
</style>
